<template>
  <div>
    <v-card
      id="history-search"
      class="mx-auto mb-5 secondary rounded-0"
      elevation="0"
    >
      <CardHeader
        title="SØG"
        :icon="!displaySearchCriterias ? openDisplayIcon : closeDisplayIcon"
        @iconClicked="displaySearchCriterias = !displaySearchCriterias"
      />
      <div v-show="displaySearchCriterias" class="px-1">
        <v-row no-gutters class="pt-2">
          <v-col
            :cols="this.$vuetify.breakpoint.smAndDown ? 6 : 2"
            class="px-2"
          >
            <v-menu
              ref="fromDatePicker"
              :close-on-content-click="true"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  ref="fromDate"
                  :value="fromDate | formatDate"
                  id="fromDate"
                  label="Fra dato"
                  persistent-hint
                  v-bind="attrs"
                  v-on="on"
                  readonly
                  outlined
                  dense
                  color="primary"
                  background-color="white"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="fromDate"
                no-title
                @input="
                  fromDatePickerVisible = false;
                  focusField('fromDate');
                "
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col
            :cols="this.$vuetify.breakpoint.smAndDown ? 6 : 2"
            class="px-2"
          >
            <v-menu
              ref="toDatePicker"
              :close-on-content-click="true"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  ref="toDate"
                  :value="toDate | formatDate"
                  id="toDate"
                  label="Til dato"
                  persistent-hint
                  v-bind="attrs"
                  v-on="on"
                  readonly
                  outlined
                  dense
                  color="primary"
                  background-color="white"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="toDate"
                no-title
                @input="
                  toDatePickerVisible = false;
                  focusField('toDate');
                "
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col
            :cols="this.$vuetify.breakpoint.smAndDown ? 12 : 4"
            class="px-2"
          >
            <v-text-field
              v-model="reference"
              label="Reference"
              outlined
              dense
              color="primary"
              background-color="white"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col
            :cols="this.$vuetify.breakpoint.smAndDown ? 12 : 4"
            class="px-2"
          >
            <v-text-field
              v-model="stopName"
              label="Navn"
              outlined
              dense
              color="primary"
              background-color="white"
            ></v-text-field>
          </v-col>
          <v-col
            :cols="this.$vuetify.breakpoint.smAndDown ? 12 : 8"
            align-self="end"
          >
            <v-card-actions>
              <v-spacer />
              <v-btn class="text-right" @click="search()">
                SØG
              </v-btn>
            </v-card-actions>
          </v-col>
        </v-row>
      </div>
      <div>
        <v-alert v-model="error" color="error" dense type="error" dismissible>
          {{ errorText }}
        </v-alert>
      </div>
    </v-card>
    <v-card
      id="history-result"
      class="mx-auto mb-5 secondary rounded-0"
      elevation="0"
    >
      <CardHeader
        title="HISTORIK"
        :enableExcel="result && result.length > 0"
        :excelData="result"
        :excelColumns="excelColumns"
        :excelFile="excelFile"
        :excelSheet="excelSheet"
      />
      <v-data-table
        :headers="headers"
        :items="result"
        :items-per-page="
          this.$vuetify.breakpoint.smAndDown
            ? 1
            : this.$vuetify.breakpoint.xl
            ? 10
            : 10
        "
        :page.sync="page"
        @page-count="pageCount = $event"
        hide-default-footer
        class="secondary accent--hover"
        dense
        :loading="loading"
        loading-text="Henter bookinger"
        no-data-text="Ingen bookinger"
        style="cursor:pointer; font-size: 8pt !important;"
        @click:row="openBooking($event)"
      >
        <template v-slot:item.stops[0].fromtime="{ item }">
          <!-- ignore eslint error regarding v-slot directive!!! -->
          <span @click="openBooking(item)">
            {{ formatDate(item.stops[0].fromtime) }}
          </span>
        </template>
        <template v-slot:item.price.totalprice="{ item }">
          <!-- ignore eslint error regarding v-slot directive!!! -->
          <v-tooltip bottom color="info" :disabled="!item.price.indicative">
            <template v-slot:activator="{ on, attrs }">
              <span
                :class="colorizePrice(item.price)"
                v-on="on"
                v-bind="attrs"
                @click="openBooking(item)"
              >
                {{ item.price.totalprice | formatDouble }}
              </span>
            </template>
            <span>Prisen er indikativ!</span>
          </v-tooltip>
        </template>
        <template v-slot:item.CO2SavedInKg="{ item }">
          <!-- ignore eslint error regarding v-slot directive!!! -->
          <v-tooltip bottom color="info">
            <template v-slot:activator="{ on, attrs }">
              <span v-on="on" v-bind="attrs" @click="openBooking(item)">
                {{ item.CO2SavedInKg | formatDouble }}
              </span>
            </template>
            <span>
              Du har sparet {{ item.CO2SavedInKg | formatDouble }} kg CO2<br />
              {{ item.distanceInKm.electric | formatDouble }} km med el-bil<br />
              {{ item.distanceInKm.regular | formatDouble }} km med almindelig
              bil
            </span>
          </v-tooltip>
        </template>
        <template v-slot:item.copybooking="{ item }">
          <!-- ignore eslint error regarding v-slot directive!!! -->
          <v-icon
            v-if="item && item.stops.length <= 2"
            class="navigation-icon"
            style="cursor:pointer;"
            @click="copyBooking(item)"
          >
            {{ copyIcon }}
          </v-icon>
        </template>
        <template v-slot:item.trackingurl="{ item }">
          <!-- ignore eslint error regarding v-slot directive!!! -->
          <v-icon
            class="navigation-icon"
            style="cursor:pointer;"
            @click="trackBooking(item)"
          >
            {{ item.trackingurl ? trackingIcon : null }}
          </v-icon>
        </template>
      </v-data-table>
      <div class="text-center pt-2">
        <v-layout>
          <v-spacer />
          <v-pagination v-model="page" :length="pageCount"> </v-pagination>
        </v-layout>
      </div>
      <BookingDialog />
    </v-card>
  </div>
</template>

<script>
import { dictionary, tools } from "../helpers";
import BookingDialog from "@/components/dialogs/BookingDialog.vue";
import CardHeader from "@/components/templates/CardHeader.vue";

export default {
  name: "HistorySearch",
  components: {
    BookingDialog,
    CardHeader
  },
  created() {
    const bookingType = this.$store.getters["user/bookingType"];
    const hidePrice = this.$store.getters["user/hidePrice"];
    this.priceVisible =
      !hidePrice &&
      bookingType !== dictionary.constants.booking_types.distribution;
    this.allowCopyBooking =
      bookingType !== dictionary.constants.booking_types.distribution;

    this.referencesVisible = !this.$store.getters["user/hideBookingReferences"];

    this.nameAddressVisible =
      bookingType == dictionary.constants.booking_types.distribution;

    if (this.nameAddressVisible) {
      if (
        this.$store.getters["user/useOwnAddressAs"] ==
        dictionary.constants.address_types.delivery
      ) {
        this.showNameAdresssOnStop = dictionary.constants.address_types.pickup;
      } else if (
        this.$store.getters["user/useOwnAddressAs"] ==
        dictionary.constants.address_types.pickup
      ) {
        this.showNameAdresssOnStop =
          dictionary.constants.address_types.delivery;
      } else {
        this.showNameAdresssOnStop = "";
      }

      this.referencesVisible = false;
    }
  },
  filters: {
    formatDate: function(date) {
      return tools.formatDate(date);
    },
    formatDouble: function(val) {
      return tools.parseDoubleToString(parseFloat(val));
    }
  },
  data() {
    return {
      displaySearchCriterias: true,
      openDisplayIcon: dictionary.mdi.open,
      closeDisplayIcon: dictionary.mdi.close,
      fromDate: tools.splitDateTime(new Date()).date,
      toDate: tools.splitDateTime(new Date()).date,
      fromDatePickerVisible: false,
      toDatePickerVisible: false,
      reference: "",
      stopName: "",
      error: false,
      errorText: "",
      loading: false,
      page: 1,
      pageCount: 0,
      result: [],
      openBookingIcon: dictionary.mdi.inspect,
      trackingIcon: dictionary.mdi.track,
      copyIcon: dictionary.mdi.copy,
      showBookingDialog: false,
      booking: null,
      referencesVisible: true,
      nameAddressVisible: false,
      showNameAdresssOnStop: "",
      priceVisible: true,
      allowCopyBooking: true,
      excelIcon: dictionary.mdi.excel,
      excelSheet: "Booking_data",
      excelFile: "GLSExpressBookinger",
      ignoreOpenBooking: false
    };
  },
  computed: {
    headers() {
      let headers = [];

      headers.push({ text: "Bookingnr.", value: "bookingno", width: "15%" });
      headers.push({ text: "Kundenr.", value: "customerno", width: "15%" });
      headers.push({ text: "Dato", value: "stops[0].fromtime", width: "15%" });
      if (this.nameAddressVisible) {
        headers.push({
          text: "Navn",
          value:
            this.showNameAdresssOnStop ==
            dictionary.constants.address_types.pickup
              ? "stops[0].address.name1"
              : "stops[1].address.name1",
          width: this.isPriceVisible ? "35%" : "50%"
        });
      }
      if (this.referencesVisible) {
        headers.push({ text: "Reference", value: "reference", width: "35%" });
      }
      headers.push({
        text: "CO2",
        value: "CO2SavedInKg",
        align: "right",
        width: "10%"
      });
      if (this.priceVisible) {
        headers.push({
          text: "Pris",
          value: "price.totalprice",
          align: "right",
          width: "10%"
        });
        headers.push({
          text: "Valuta",
          value: "price.currency",
          width: "5%",
          sortable: false
        });
      }
      if (this.allowCopyBooking) {
        headers.push({
          text: "",
          value: "copybooking",
          width: "5%",
          sortable: false
        }); //copy booking
      }
      headers.push({
        text: "",
        value: "trackingurl",
        width: "5%",
        sortable: false
      }); //T&T
      return headers;
    },
    excelColumns() {
      let columns = [];

      columns.push({ label: "Bookingnr.", field: "bookingno" });
      columns.push({ label: "Kundenr.", field: "customerno" });
      if (this.referencesVisible) {
        columns.push({ label: "Reference", field: "reference" });
      }
      columns.push({
        label: "Dato",
        field: "stops",
        dataFormat: value => {
          const date = value[0].fromtime.substr(0, 10);
          return tools.formatDate(date);
        }
      });
      columns.push({ label: "Biltype", field: "cartype" });
      columns.push({
        label: "CO2 besparelse (kg)",
        field: "CO2SavedInKg",
        dataFormat: value => {
          return tools.parseDoubleToString(parseFloat(value));
        }
      });
      columns.push({
        label: "KM el-bil",
        field: "distanceInKm",
        dataFormat: value => {
          return tools.parseDoubleToString(parseFloat(value.electric));
        }
      });
      columns.push({
        label: "KM alm. bil",
        field: "distanceInKm",
        dataFormat: value => {
          return tools.parseDoubleToString(parseFloat(value.regular));
        }
      });
      if (this.priceVisible) {
        columns.push({
          label: "Pris",
          field: "price",
          dataFormat: value => {
            return tools.parseDoubleToString(parseFloat(value.totalprice));
          }
        });
      }
      columns.push({
        label: "Første stop",
        field: "stops",
        dataFormat: value => {
          return value != null && value.length > 0
            ? value[0].address.name1 +
                ", " +
                value[0].address.address1 +
                ", " +
                value[0].address.countrycode +
                "-" +
                value[0].address.zip +
                " " +
                value[0].address.city
            : "";
        }
      });
      columns.push({
        label: "Sidste stop",
        field: "stops",
        dataFormat: value => {
          return value != null && value.length > 0
            ? value[value.length - 1].address.name1 +
                ", " +
                value[value.length - 1].address.address1 +
                ", " +
                value[value.length - 1].address.countrycode +
                "-" +
                value[value.length - 1].address.zip +
                " " +
                value[value.length - 1].address.city
            : "";
        }
      });
      columns.push({
        label: "Antal stop",
        field: "stops",
        dataFormat: value => {
          return value != null ? value.length : 0;
        }
      });
      return columns;
    }
  },
  methods: {
    priceIndicative(price) {
      return (
        price &&
        price.pricestatus === dictionary.constants.price_status.indicative
      );
    },
    colorizePrice(price) {
      if (price.indicative) {
        return "warning--text";
      }
      return "";
    },
    formatDate(dateTime) {
      if (dateTime) {
        return tools.formatDate(tools.splitDateTime(new Date(dateTime)).date);
      }
      return "";
    },
    async search() {
      this.validate();

      if (!this.error) {
        this.loading = true;
        this.result = [];
        const { fromDate, toDate, reference, stopName } = this;
        await this.$store
          .dispatch("booking/searchBookings", {
            fromDate,
            toDate,
            reference,
            stopName
          })
          .then(
            response => {
              if (response) {
                response.result.bookings.forEach(booking => {
                  if (booking) {
                    if (booking.price) {
                      booking.price["indicative"] = this.priceIndicative(
                        booking.price
                      );
                    }

                    booking["CO2SavedInKg"] = 0;
                    booking["distanceInKm"] = { electric: 0, regular: 0 };

                    const customerIds = [];
                    const fromDate = null;
                    const toDate = null;
                    const bookingId = booking.bookingno;

                    this.$store
                      .dispatch("booking/calculateCO2Footprint", {
                        customerIds,
                        fromDate,
                        toDate,
                        bookingId
                      })
                      .then(
                        response => {
                          if (response) {
                            response.forEach(co2Footprint => {
                              booking.CO2SavedInKg +=
                                co2Footprint.co2_saved / 1000;
                              if (co2Footprint.electric) {
                                booking.distanceInKm.electric +=
                                  co2Footprint.distance / 1000;
                              } else {
                                booking.distanceInKm.regular +=
                                  co2Footprint.distance / 1000;
                              }
                            });
                          }
                        },
                        error => {
                          console.error(error);
                        }
                      );
                    this.result.push(booking);
                  }
                });
              }
            },
            error => {
              console.error(error);
            }
          );

        this.excelSheet = "Booking data";
        this.excelFile = "GLSExpressBookinger";

        if (this.result && this.result.length) {
          this.excelSheet = fromDate + "_" + toDate;
          const d = new Date();
          this.excelFile = "GLSExpressBookinger_" + d.getTime();
        }
        this.loading = false;
      }
    },
    validate() {
      this.errorText = null;
      this.error = false;

      let from = this.fromDate ? new Date(this.fromDate) : null;
      let to = this.toDate ? new Date(this.toDate) : null;
      if (from.getTime() > to.getTime()) {
        this.errorText = "Vælg fra dato tidligere end til dato.";
        this.error = true;
      }
    },
    trackBooking(booking) {
      window.open(booking.trackingurl, "_blank");
      this.ignoreOpenBooking = true;
    },
    openBooking(booking) {
      if (!this.ignoreOpenBooking) {
        this.$store.commit("booking/setBookingForDisplay", booking);
        this.$store.commit("booking/setDisplayBookingDialog", true);
      }
      this.ignoreOpenBooking = false;
    },
    copyBooking(booking) {
      this.$store.commit("booking/resetBooking");
      this.$store.commit("booking/setBooking", booking);
      this.ignoreOpenBooking = true;
      this.$router.push("/booking");
    },
    focusField(fieldName) {
      if (this.$refs[fieldName]) {
        this.$refs[fieldName].focus();
      }
    }
  }
};
</script>
